// AlertCenter — Dropdown alert panel with severity chips, dismiss, and entity links.

import React, { useState } from 'react';
import { Bell, X, AlertTriangle, Info, AlertOctagon } from 'lucide-react';
import { getStatusStyle } from '../../config/statusColors';

const severityIcon = {
  info: Info,
  warning: AlertTriangle,
  critical: AlertOctagon,
};

const severityColor = {
  info: 'text-zinc-500',
  warning: 'text-zinc-700',
  critical: 'text-red-700',
};

const AlertCenter = ({ alerts = [], onDismiss }) => {
  const [open, setOpen] = useState(false);
  const active = alerts.filter(a => !a.dismissed);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative flex items-center gap-1 px-3 py-2 border border-zinc-200 rounded-none text-xs hover:bg-zinc-50 transition-colors"
      >
        <Bell className="w-4 h-4 text-zinc-500" />
        {active.length > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
            {active.length > 9 ? '9+' : active.length}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

          {/* Dropdown */}
          <div className="absolute right-0 top-full mt-1 w-96 bg-white border border-zinc-200 rounded-none shadow-lg z-50 max-h-96 overflow-y-auto">
            <div className="p-3 border-b border-zinc-100 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                Alerts ({active.length})
              </span>
              {active.length > 0 && (
                <button
                  onClick={() => active.forEach(a => onDismiss?.(a.id))}
                  className="font-mono text-[10px] text-zinc-400 hover:text-zinc-600"
                >
                  Dismiss All
                </button>
              )}
            </div>

            {active.length === 0 && (
              <div className="p-6 text-center text-zinc-400 text-xs">
                No active alerts
              </div>
            )}

            {active.map(alert => {
              const Icon = severityIcon[alert.severity] || Info;
              const color = severityColor[alert.severity] || 'text-zinc-500';
              return (
                <div key={alert.id} className="p-3 border-b border-zinc-50 hover:bg-zinc-50 flex items-start gap-2">
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${color}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-700 leading-tight">{alert.message}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider border rounded-none"
                        style={{ ...getStatusStyle(alert.severity), borderColor: getStatusStyle(alert.severity).color }}
                      >
                        {alert.severity}
                      </span>
                      <span className="font-mono text-[9px] text-zinc-400">
                        {new Date(alert.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {alert.entityId && (
                        <span className="font-mono text-[9px] text-zinc-400">{alert.entityId}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDismiss?.(alert.id); }}
                    className="text-zinc-300 hover:text-zinc-500 flex-shrink-0"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default AlertCenter;
